/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import VendorManager from './VendorManager';
import VendorInvoiceReview from './VendorInvoiceReview';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Building2, Users, DollarSign, TrendingUp, UserPlus, 
  Home, Settings, LogOut, PlusCircle, Wallet, Calculator, Activity,
  HelpCircle, Store
} from 'lucide-react';
import PayoutTab from './landlord/PayoutTab';
import PropertyManagement from './landlord/PropertyManagement';
import RentalListingManager from './landlord/RentalListingManager';
import ReferralDashboard from './ReferralDashboard';
import PLReports from './PLReports';
import FinancialAnalytics from './FinancialAnalytics';
import StaffManagement from './shared/StaffManagement';

const LandlordDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    total_tenants: 0,
    total_properties: 0,
    total_collected: 0,
    total_due: 0,
    recent_payments: [],
    maintenance_requests: 0
  });
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showProcessPayment, setShowProcessPayment] = useState(false);
  const [selectedTenantForPayment, setSelectedTenantForPayment] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    tenantId: '',
    tenantName: '',
    unitNumber: '',
    amount: 0,
    method: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [invitationCode, setInvitationCode] = useState('');
  const [tenants, setTenants] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
    fetchProperties();
  }, []);

  // Fetch tenants when switching to Tenants tab
  useEffect(() => {
    if (activeTab === 'tenants') {
      fetchTenants();
    }
  }, [activeTab]);
  
  // Fetch units when property is selected
  useEffect(() => {
    if (selectedProperty) {
      fetchUnits(selectedProperty);
    }
  }, [selectedProperty]);

  const fetchDashboardData = async () => {
    try {
      const { data } = await api.get("/landlord/dashboard");
      setDashboardData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading landlord data:", error);
      toast.error("Failed to load dashboard data");
      setLoading(false);
    }
  };
  
  const fetchProperties = async () => {
    try {
      const { data } = await api.get("/landlord/properties");
      setProperties(data.properties || []);
    } catch (error) {
      console.error("Error loading properties:", error);
    }
  };
  
  const fetchUnits = async (propertyId) => {
    try {
      const { data } = await api.get(`/landlord/properties/${propertyId}/units`);
      setUnits(data.units || []);
    } catch (error) {
      console.error("Error loading units:", error);
      setUnits([]);
    }
  };

  const fetchTenants = async () => {
    setLoadingTenants(true);
    try {
      const { data } = await api.get("/landlord/tenants");
      setTenants(data.tenants || []);
      setInvitations(data.invites || []);
      setLoadingTenants(false);
    } catch (error) {
      console.error("Error loading tenants:", error);
      toast.error("Failed to load tenants");
      setLoadingTenants(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (window.confirm("Are you sure you want to delete this tenant? This action cannot be undone.")) {
      try {
        await api.delete(`/landlord/tenants/${tenantId}`);
        toast.success("Tenant deleted successfully");
        fetchTenants(); // Refresh list
      } catch (error) {
        console.error("Error deleting tenant:", error);
        toast.error("Failed to delete tenant");
      }
    }
  };

  const handleDeleteInvitation = async (inviteId) => {
    if (window.confirm("Are you sure you want to revoke this invitation?")) {
      try {
        await api.delete(`/landlord/invites/${inviteId}`);
        toast.success("Invitation revoked successfully");
        fetchTenants(); // Refresh list
      } catch (error) {
        console.error("Error revoking invitation:", error);
        toast.error("Failed to revoke invitation");
      }
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const tenantData = {
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      unit_number: formData.get('unit_number'),
      rent_amount: parseFloat(formData.get('rent_amount')),
      lease_start_date: formData.get('lease_start_date')
    };
    
    try {
      const { data } = await api.post("/landlord/tenants/create", tenantData);
      toast.success('Tenant added successfully!');
      setShowAddTenant(false);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error("Error adding tenant:", error);
      toast.error("Failed to add tenant");
    }
  };

  const handleGenerateInvite = async () => {
    try {
      if (!selectedProperty) {
        toast.error("Please select a property first");
        return;
      }
      
      const { data } = await api.post("/landlord/tenants/invite", {
        property_id: selectedProperty,
        unit_id: selectedUnit || null
      });
      setInvitationCode(data.invite_code);
      toast.success(`Invitation code generated: ${data.invite_code}`);
      fetchTenants(); // Refresh invitations list
    } catch (error) {
      console.error("Error generating code:", error);
      toast.error("Failed to generate invitation code");
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('org_type');
    navigate('/landlord/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Landlord Dashboard</h1>
              <p className="text-sm text-gray-600">{dashboardData?.organization?.name || 'Property Management'}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-emerald-100 text-emerald-800">Landlord</Badge>
              <Button 
                onClick={() => navigate('/kb')} 
                variant="ghost" 
                size="sm"
                data-testid="landlord-dashboard-help-btn"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                Help
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Properties</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.total_properties || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Units</CardTitle>
              <Home className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.total_units || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.active_tenants || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${dashboardData?.monthly_revenue?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground">Rent Collected</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowAddTenant(true)}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add Tenant
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => setShowAddProperty(true)}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Property
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => setShowProcessPayment(true)}
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Process Payment
          </Button>
          <Button 
            className="bg-orange-600 hover:bg-orange-700"
            onClick={() => setActiveTab('reports')}
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            View Reports
          </Button>
        </div>

        {/* Add Property Dialog */}
        {showAddProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add New Property</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const propertyData = {
                    name: formData.get('property_name'),
                    address: formData.get('address'),
                    building_count: parseInt(formData.get('building_count')),
                    unit_count: parseInt(formData.get('unit_count'))
                  };
                  
                  try {
                    await api.post('/landlord/properties', propertyData);
                    toast.success('Property added successfully!');
                    setShowAddProperty(false);
                    fetchProperties(); // Refresh properties list
                  } catch (error) {
                    console.error('Error adding property:', error);
                    toast.error(error.response?.data?.detail || 'Failed to add property');
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Property Name</label>
                    <input type="text" name="property_name" required className="w-full px-3 py-2 border rounded" placeholder="e.g., Sunset Apartments" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <input type="text" name="address" required className="w-full px-3 py-2 border rounded" placeholder="123 Main St" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Buildings</label>
                      <input type="number" name="building_count" required className="w-full px-3 py-2 border rounded" defaultValue="1" min="1" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Units</label>
                      <input type="number" name="unit_count" required className="w-full px-3 py-2 border rounded" defaultValue="1" min="0" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">Add Property</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddProperty(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Tenant Dialog */}
        {showAddTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Add New Tenant</CardTitle>
              </CardHeader>
              <CardContent>
                {!invitationCode ? (
                  <form onSubmit={handleAddTenant} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">First Name</label>
                        <input type="text" name="first_name" required className="w-full px-3 py-2 border rounded" placeholder="John" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last Name</label>
                        <input type="text" name="last_name" required className="w-full px-3 py-2 border rounded" placeholder="Doe" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input type="email" name="email" required className="w-full px-3 py-2 border rounded" placeholder="tenant@example.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone Number</label>
                      <input type="tel" name="phone" className="w-full px-3 py-2 border rounded" placeholder="+1 (555) 000-0000" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Property</label>
                      <select 
                        value={selectedProperty} 
                        onChange={(e) => setSelectedProperty(e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        required
                      >
                        <option value="">Select a property</option>
                        {properties.map((property) => (
                          <option key={property.id} value={property.id}>
                            {property.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedProperty && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Unit (Optional)</label>
                        <select 
                          value={selectedUnit} 
                          onChange={(e) => setSelectedUnit(e.target.value)}
                          className="w-full px-3 py-2 border rounded"
                        >
                          <option value="">Any available unit</option>
                          {units.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.unit_number}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-1">Unit Number</label>
                      <input type="text" name="unit_number" required className="w-full px-3 py-2 border rounded" placeholder="e.g., 101" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Monthly Rent ($)</label>
                      <input type="number" name="rent_amount" required className="w-full px-3 py-2 border rounded" placeholder="1500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Lease Start Date</label>
                      <input type="date" name="lease_start_date" required className="w-full px-3 py-2 border rounded" />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1">Add Tenant</Button>
                      <Button type="button" variant="outline" onClick={() => {
                        handleGenerateInvite();
                      }}>Generate Code Only</Button>
                      <Button type="button" variant="outline" onClick={() => {
                        setShowAddTenant(false);
                        setInvitationCode('');
                      }}>Cancel</Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Tenant Invitation Code</p>
                      <p className="text-3xl font-bold text-emerald-600 tracking-wider mb-4">{invitationCode}</p>
                      <Button 
                        onClick={() => {
                          // Fallback copy method for when Clipboard API is blocked
                          const textarea = document.createElement('textarea');
                          textarea.value = invitationCode;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          try {
                            document.execCommand('copy');
                            toast.success('Code copied to clipboard!');
                          } catch (err) {
                            toast.error('Please copy the code manually: ' + invitationCode);
                          }
                          document.body.removeChild(textarea);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Copy Code
                      </Button>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                      <p className="font-medium mb-1">Next Steps:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Share this code with your tenant</li>
                        <li>Direct them to the tenant registration page</li>
                        <li>They'll use this code to complete their registration</li>
                      </ol>
                    </div>
                    <Button 
                      onClick={() => {
                        setInvitationCode('');
                        setShowAddTenant(false);
                      }}
                      className="w-full"
                    >
                      Done
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Process Payment Dialog */}
        {showProcessPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Process Rent Payment</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    // TODO: Implement actual payment processing endpoint
                    // await api.post('/landlord/payments', paymentForm);
                    toast.success('Payment processed successfully!');
                    setShowProcessPayment(false);
                    setPaymentForm({
                      tenantId: '',
                      tenantName: '',
                      unitNumber: '',
                      amount: 0,
                      method: '',
                      date: new Date().toISOString().split('T')[0]
                    });
                  } catch (error) {
                    toast.error('Failed to process payment');
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Select Tenant *</label>
                    <select
                      value={selectedTenantForPayment}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        setSelectedTenantForPayment(selectedId);
                        
                        const tenant = tenants.find(t => t.id === selectedId);
                        if (tenant) {
                          setPaymentForm({
                            ...paymentForm,
                            tenantId: tenant.id,
                            tenantName: tenant.name,
                            unitNumber: tenant.unit_number || 'N/A',
                            amount: 350 // Default rent, can be adjusted
                          });
                        }
                      }}
                      required
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">-- Select Tenant --</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name} - Unit {tenant.unit_number || 'N/A'}
                        </option>
                      ))}
                    </select>
                    {tenants.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No tenants found. Add tenants first.</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Unit Number</label>
                    <input
                      type="text"
                      value={paymentForm.unitNumber}
                      readOnly
                      className="w-full px-3 py-2 border rounded bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Amount ($) *</label>
                    <input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border rounded"
                      placeholder="1500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Method *</label>
                    <select
                      value={paymentForm.method}
                      onChange={(e) => setPaymentForm({...paymentForm, method: e.target.value})}
                      required
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="">Select method</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="card">Credit/Debit Card</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Date *</label>
                    <input
                      type="date"
                      value={paymentForm.date}
                      onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})}
                      required
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1" disabled={!selectedTenantForPayment}>
                      Process Payment
                    </Button>
                    <Button type="button" variant="outline" onClick={() => {
                      setShowProcessPayment(false);
                      setSelectedTenantForPayment('');
                      setPaymentForm({
                        tenantId: '',
                        tenantName: '',
                        unitNumber: '',
                        amount: 0,
                        method: '',
                        date: new Date().toISOString().split('T')[0]
                      });
                    }}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="properties">Properties</TabsTrigger>
            <TabsTrigger value="marketplace" className="text-emerald-600">Marketplace</TabsTrigger>
            <TabsTrigger value="tenants">Tenants</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
            <TabsTrigger value="analytics" className="text-indigo-600">Analytics</TabsTrigger>
            <TabsTrigger value="tax-pl" className="text-purple-600">Tax & P&L</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="vendors" className="text-amber-600">Vendors</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="staff" className="text-red-600">Staff</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>Property Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Welcome to your landlord dashboard</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="properties">
            <PropertyManagement />
          </TabsContent>

          <TabsContent value="marketplace">
            <RentalListingManager />
          </TabsContent>

          <TabsContent value="tenants">
            <div className="space-y-6">
              {/* Tenant List Section */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Active Tenants</CardTitle>
                  <Button onClick={() => setShowAddTenant(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite New Tenant
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingTenants ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                  ) : tenants.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Phone</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Property</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Unit #</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Registered</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tenants.map((tenant) => (
                            <tr key={tenant.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">{tenant.name}</td>
                              <td className="py-3 px-4 text-gray-600">{tenant.email}</td>
                              <td className="py-3 px-4 text-gray-600">{tenant.phone || 'N/A'}</td>
                              <td className="py-3 px-4 text-gray-600">{tenant.property_name || 'Unassigned'}</td>
                              <td className="py-3 px-4 text-gray-600 font-mono">{tenant.unit_number || 'N/A'}</td>
                              <td className="py-3 px-4 text-gray-600">
                                {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {tenant.status || 'Active'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteTenant(tenant.id)}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No tenants yet. Invite your first tenant to get started!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invitation Codes Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Invitation Codes</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingTenants ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                  ) : invitations.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Code</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Expires</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Used At</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invitations.map((invite) => (
                            <tr key={invite.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-mono font-bold">{invite.code}</td>
                              <td className="py-3 px-4">
                                <Badge 
                                  className={
                                    invite.status === 'active' ? 'bg-green-100 text-green-800' :
                                    invite.status === 'used' ? 'bg-blue-100 text-blue-800' :
                                    invite.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                    'bg-red-100 text-red-800'
                                  }
                                >
                                  {invite.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {invite.used_at ? new Date(invite.used_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-3 px-4 text-right space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const textarea = document.createElement('textarea');
                                    textarea.value = invite.code;
                                    textarea.style.position = 'fixed';
                                    textarea.style.opacity = '0';
                                    document.body.appendChild(textarea);
                                    textarea.select();
                                    try {
                                      document.execCommand('copy');
                                      toast.success('Code copied!');
                                    } catch (err) {
                                      toast.error('Please copy manually: ' + invite.code);
                                    }
                                    document.body.removeChild(textarea);
                                  }}
                                  disabled={invite.status !== 'active'}
                                >
                                  Copy
                                </Button>
                                {invite.status === 'active' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteInvitation(invite.id)}
                                  >
                                    Revoke
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No invitation codes generated yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Rent Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Track rent payments and financial transactions</p>
                <Button onClick={() => setShowProcessPayment(true)}>
                  <DollarSign className="mr-2 h-4 w-4" />
                  Record Payment
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Wallet className="mr-2 h-5 w-5" />
                  Payout Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PayoutTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <FinancialAnalytics 
              orgId={dashboardData?.organization?.id} 
              orgName={dashboardData?.organization?.name || "My Properties"}
              orgType="landlord"
            />
            <div className="mt-6">
              <VendorInvoiceReview />
            </div>
          </TabsContent>

          <TabsContent value="tax-pl">
            <PLReports 
              orgId={dashboardData?.organization?.id} 
              orgName={dashboardData?.organization?.name || "My Organization"}
              orgCountry={dashboardData?.organization?.country || "US"}
            />
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle>Financial Reports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-24 flex flex-col items-center justify-center">
                    <TrendingUp className="h-6 w-6 mb-2" />
                    <span>Revenue Report</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col items-center justify-center">
                    <DollarSign className="h-6 w-6 mb-2" />
                    <span>Payment History</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col items-center justify-center">
                    <Users className="h-6 w-6 mb-2" />
                    <span>Occupancy Report</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex flex-col items-center justify-center">
                    <Building2 className="h-6 w-6 mb-2" />
                    <span>Property Performance</span>
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-4">Click on any report to generate and download</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-medium mb-3">Profile Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input type="text" className="w-full px-3 py-2 border rounded" defaultValue={dashboardData?.organization?.name || "Landlord"} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input type="email" className="w-full px-3 py-2 border rounded" defaultValue="landlord@caribbeanproperties.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone</label>
                      <input type="tel" className="w-full px-3 py-2 border rounded" placeholder="+1 (555) 000-0000" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-3">Notifications</h3>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" defaultChecked />
                      <span className="text-sm">Email notifications for new payments</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" defaultChecked />
                      <span className="text-sm">Email notifications for maintenance requests</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-sm">SMS notifications</span>
                    </label>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium mb-3">Security</h3>
                  <Button variant="outline">Change Password</Button>
                </div>
                <Button className="w-full">Save Settings</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="vendors">
            <VendorManager apiPrefix="/landlord" />
          </TabsContent>

          <TabsContent value="staff">
            <StaffManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LandlordDashboard;
